import escapeHtml from 'escape-html';
import { Descendant, Text } from 'slate';
import { isElement, isImage } from '../helpers';

// Browser-compatible EJS-like template renderer
// Replaces ejs package which is Node.js-only
const ejsRender = (str: string, data: Record<string, any>, options?: { escape?: (markup: any) => string }): string => {
  const escapeFn = options?.escape || ((s: string) => escapeHtml(String(s)));

  // Build the template function
  let jsCode = 'let html="";';
  let i = 0;
  let match: RegExpExecArray | null;
  const regex = /<%(-?)([\s\S]*?)%(-?)>/g;

  while ((match = regex.exec(str)) !== null) {
    const [fullMatch, beforeSpace, code, afterSpace] = match;
    const textBefore = str.slice(i, match.index);

    // Add text before the EJS tag (escape it by default)
    if (textBefore) {
      // Escape special characters for JS string literal
      const escapedText = textBefore
        .replace(/\\/g, '\\\\')
        .replace(/'/g, "\\'")
        .replace(/\r/g, '\\r')
        .replace(/\n/g, '\\n');
      jsCode += `html+='${escapedText}';`;
    }

    if (code.startsWith('=')) {
      // Escaped output: <%= %>
      const expr = code.slice(1).trim();
      jsCode += `html+=escapeFn(${expr});`;
    } else if (code.startsWith('-')) {
      // Unescaped output: <%- %>
      const expr = code.slice(1).trim();
      jsCode += `html+=${expr};`;
    } else {
      // Code execution: <% %>
      jsCode += code.trim() + ';';
    }

    i = regex.lastIndex;
  }

  // Add remaining text after last EJS tag
  if (i < str.length) {
    const textAfter = str.slice(i)
      .replace(/\\/g, '\\\\')
      .replace(/'/g, "\\'")
      .replace(/\r/g, '\\r')
      .replace(/\n/g, '\\n');
    jsCode += `html+='${textAfter}';`;
  }

  jsCode += 'return html;';

  // Create and execute the function
  const renderFn = new Function('data', 'escapeFn', `with(data){${jsCode}}`);
  return renderFn(data, escapeFn);
};

// Expose a simple ejs-like interface
const ejs = {
  render: ejsRender
};

// Export the template function directly for use in Newsletter.tsx
export { ejsRender };

// Serialize Slate state into valid HTML, with varying serialization for previewing
const serialize = (node: Descendant, isPreview: boolean): string => {
	// Add marks to text
	if (Text.isText(node)) {
		let html = escapeHtml(node.text);
		if (node.bold) {
			html = `<strong>${html}</strong>`;
		}
		if (node.italic) {
			html = `<em>${html}</em>`;
		}
		if (node.underline) {
			html = `<u>${html}</u>`;
		}
		if (node.strikethrough) {
			html = `<s>${html}</s>`;
		}
		return html;
	}

	if (isImage(node)) {
		return `<img src="${escapeHtml(node.src)}" />`;
	}

	// Recursively serialize nested elements
	const children = node.children
		.map((child) => serialize(child, isPreview))
		.join('');

	if (!isElement(node)) {
		return children;
	}

	switch (node.type) {
		case 'bulleted-list':
			return `<ul>${children}</ul>`;
		case 'heading1':
			return `<h1>${children}</h1>`;
		case 'heading2':
			return `<h2>${children}</h2>`;
		case 'heading3':
			return `<h3>${children}</h3>`;
		case 'heading4':
			return `<h4>${children}</h4>`;
		case 'heading5':
			return `<h5>${children}</h5>`;
		case 'heading6':
			return `<h6>${children}</h6>`;
		case 'link':
			return `<a href="${escapeHtml(node.href)}">${children}</a>`;
		case 'ejs-button':
			// Render EJS button
			return ejs.render(node.ejs, node.data, {
				// Do not escape EJS links
				escape: (markup: any) => markup,
			});
		case 'ejs-placeholder':
			// Serialize placeholder text when previewing, otherwise the given ejs
			return `<span>${isPreview ? node.placeholder : node.ejs}</span>`;
		case 'list-item':
			return `<li>${children}</li>`;
		case 'numbered-list':
			return `<ol>${children}</ol>`;
		case 'paragraph':
			return `<p>${children}</p>`;
		case 'pre':
			return `<pre>${children}</pre>`;
		default:
			return children;
	}
};

// Take state array and convert it to valid serialization input
export const serializeHtml = (
	value: Descendant[],
	isPreview: boolean
): string => serialize({ children: value } as Descendant, isPreview);
